import { useEffect, useRef, useState } from "react";
import { TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useDragOrClick, useDragParentGroup } from "./useDragParentGroup";
import MaxVideoLimitHint from "../MaxVideoLimitHint";

export default function SchemeSceneContent({
  parentGroupId,
  hasScreenshot,
  imageKey,
  imageSrc,
  hasImagePreview = false,
  imagePreviewSrc,
  buttonCount = 0,
  maxButtons = 2,
  isTrainer = false,
  contentMode = "video",
  contentText = "",
  textPanelTitle = "Текст",
  textPlaceholder = "Введите текст",
  onContentModeChange,
  onContentTextChange,
  onVideoSelected,
  onImageSelected,
  onAddButton,
  buttonText,
  onButtonTextChange,
  availableMessengers = [],
}) {
  const [panel, setPanel] = useState(null);
  const [imgError, setImgError] = useState(false);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  const startGroupDrag = useDragParentGroup(parentGroupId);
  const openVideoPanel = useDragOrClick(parentGroupId, () => setPanel("video"));
  const openImagePanel = useDragOrClick(parentGroupId, () => setPanel("image"));
  const openButtonPanel = useDragOrClick(parentGroupId, () => setPanel("button"));
  const openTextPanel = useDragOrClick(parentGroupId, () => setPanel("text"));

  const canAddButton = buttonCount < maxButtons;
  const isTextMode = contentMode === "text";
  const isVideoMode = contentMode === "video";
  const isImageMode = contentMode === "image";
  const showVideoScreenshot = isVideoMode && hasScreenshot && imageSrc && !imgError;
  const showImagePreview = isImageMode && hasImagePreview && imagePreviewSrc && !imgError;

  useEffect(() => {
    setImgError(false);
  }, [imageSrc, imagePreviewSrc, imageKey]);

  const closePanel = () => setPanel(null);

  const handleVideoPick = (event) => {
    onVideoSelected?.(event);
    closePanel();
  };

  const handleImagePick = (event) => {
    onImageSelected?.(event);
    closePanel();
  };

  const handleAddButton = () => {
    onAddButton();
    closePanel();
  };

  const mediaClassName = [
    "scheme-scene__media-wrap",
    "nodrag",
    "nopan",
    isTextMode
      ? "scheme-scene__media-wrap--text"
      : showVideoScreenshot || showImagePreview
        ? "scheme-scene__media-wrap--filled"
        : imgError
          ? "scheme-scene__media-wrap--error"
          : "scheme-scene__media-wrap--empty",
  ].join(" ");

  const handleCardPointerDown = (event) => {
    if (
      event.target.closest(
        ".scheme-scene__media-wrap, .scheme-scene__tool, .scheme-scene__overlay, .scheme-scene__mode"
      )
    ) {
      return;
    }
    startGroupDrag(event);
  };

  return (
    <div className="scheme-scene__card" onPointerDown={handleCardPointerDown}>
      <div
        className={mediaClassName}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div
          className="scheme-scene__mode scheme-scene__mode--overlay nodrag nopan"
          onPointerDown={(event) => event.stopPropagation()}
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={contentMode}
            onChange={(_, value) => {
              if (value) onContentModeChange?.(value);
            }}
          >
            <ToggleButton value="text" aria-label="Текст">
              <TextFieldsOutlinedIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="video" aria-label="Видео">
              <VideocamOutlinedIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="image" aria-label="Картинка">
              <ImageOutlinedIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </div>

        {isTextMode ? (
          <div className="scheme-scene__text-preview nodrag nopan">
            {contentText?.trim() ? contentText : textPlaceholder}
          </div>
        ) : null}

        {showVideoScreenshot ? (
          <img
            key={imageKey}
            src={imageSrc}
            alt=""
            className="scheme-scene__thumb"
            onError={() => setImgError(true)}
          />
        ) : null}

        {showImagePreview ? (
          <img
            key={`${imageKey}-content`}
            src={imagePreviewSrc}
            alt=""
            className="scheme-scene__thumb"
            onError={() => setImgError(true)}
          />
        ) : null}

        {panel && (
          <div
            className="scheme-scene__overlay nodrag nopan"
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="scheme-scene__overlay-close"
              onClick={closePanel}
              aria-label="Закрыть"
            >
              <CloseIcon fontSize="small" />
            </button>

            {panel === "button" ? (
              <div className="scheme-scene__overlay-body">
                <span className="scheme-scene__overlay-title">
                  {isTrainer ? "Вариант ответа" : "Новая кнопка"}
                </span>
                <TextField
                  placeholder={isTrainer ? "Текст ответа" : "Текст кнопки"}
                  variant="outlined"
                  size="small"
                  fullWidth
                  autoFocus
                  value={buttonText}
                  onChange={(event) => onButtonTextChange(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") handleAddButton();
                  }}
                />
                <button
                  type="button"
                  className="scheme-scene__overlay-submit"
                  onClick={handleAddButton}
                >
                  Добавить
                </button>
              </div>
            ) : panel === "text" ? (
              <div className="scheme-scene__overlay-body">
                <span className="scheme-scene__overlay-title">{textPanelTitle}</span>
                <TextField
                  placeholder={textPlaceholder}
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={3}
                  autoFocus
                  value={contentText}
                  onChange={(event) => onContentTextChange(event.target.value)}
                />
                <button
                  type="button"
                  className="scheme-scene__overlay-submit"
                  onClick={closePanel}
                >
                  Готово
                </button>
              </div>
            ) : panel === "image" ? (
              <div className="scheme-scene__overlay-body">
                <span className="scheme-scene__overlay-title">Загрузка картинки</span>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImagePick}
                />
                <button
                  type="button"
                  className="scheme-scene__overlay-submit"
                  onClick={() => imageInputRef.current?.click()}
                >
                  Выбрать файл
                </button>
              </div>
            ) : (
              <div className="scheme-scene__overlay-body">
                <span className="scheme-scene__overlay-title">Загрузка видео</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  hidden
                  onChange={handleVideoPick}
                />
                <button
                  type="button"
                  className="scheme-scene__overlay-submit"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Выбрать файл
                </button>
                <MaxVideoLimitHint availableMessengers={availableMessengers} />
              </div>
            )}
          </div>
        )}
      </div>

      {!panel && (
        <div
          className={`scheme-scene__toolbar${
            !canAddButton ? " scheme-scene__toolbar--single" : ""
          }${isTrainer ? " scheme-scene__toolbar--trainer" : ""}`}
        >
          {isTextMode ? (
            <button
              type="button"
              className="scheme-scene__tool"
              onPointerDown={openTextPanel}
            >
              <TextFieldsOutlinedIcon fontSize="inherit" />
              {isTrainer ? "Вопрос" : "Текст"}
            </button>
          ) : isImageMode ? (
            <button
              type="button"
              className="scheme-scene__tool"
              onPointerDown={openImagePanel}
            >
              <ImageOutlinedIcon fontSize="inherit" />
              {hasImagePreview ? "Заменить" : "Картинка"}
            </button>
          ) : (
            <button
              type="button"
              className="scheme-scene__tool"
              onPointerDown={openVideoPanel}
            >
              <VideocamOutlinedIcon fontSize="inherit" />
              {hasScreenshot ? "Заменить" : "Видео"}
            </button>
          )}

          {canAddButton ? (
            <button
              type="button"
              className="scheme-scene__tool"
              onPointerDown={openButtonPanel}
            >
              <AddIcon fontSize="inherit" />
              {isTrainer ? "Ответ" : "Кнопка"}
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}
